import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { UpdateManyParams, useNotify, useRefresh, useTranslate, useUnselectAll } from 'react-admin';

import { CustomCalendar } from '@/components/CustomCalendar';
import { employeesDataProvider } from '@/dataProvider';
import { IEmployeesTerminateRequestData } from '@/types';

type SuspendType = 'manual' | 'calendar';

interface ISuspendEmployeesProps {
  handleClose: () => void;
  isOpen: boolean;
  ids: string[];
}

function SuspendEmployees({ handleClose, isOpen, ids }: ISuspendEmployeesProps): JSX.Element {
  const t = useTranslate();
  const notify = useNotify();
  const unselectAll = useUnselectAll('employees');
  const refresh = useRefresh();

  const [suspendType, setSuspendType] = useState<SuspendType>('manual');
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const resetForm = () => {
    setSuspendType('manual');
    setSelectedDates([]);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const onTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = (event.target as HTMLInputElement).value as SuspendType;

    setSuspendType(type);

    if (type !== 'calendar') {
      setSelectedDates([]);
    }
  };

  const handleCalendarChange = (dates: Date[]) => {
    setSelectedDates(dates);
  };

  const handleRemoveSelectedDay = (dateToRemove: Date): void => {
    const newDates = selectedDates.filter(date => date.getTime() !== dateToRemove.getTime());
    setSelectedDates(newDates);
  };

  const handleApply = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      const payload: UpdateManyParams<IEmployeesTerminateRequestData> = { ids, data: {} };
      if (suspendType === 'manual') {
        payload.data.is_terminated = true;
      } else {
        payload.data.termination_dates = selectedDates;
      }

      await employeesDataProvider.terminateEmployees(payload);

      notify('resources.reviews.notification.approved_success', {
        type: 'info',
      });
      unselectAll();
      refresh();
      handleClose();
    } catch {
      notify('resources.reviews.notification.approved_error', {
        type: 'error',
      });
    }

    setIsLoading(false);
  }, [suspendType, selectedDates]);

  const handleCancel = (): void => {
    handleClose();
  };

  const buttonDisabled = (suspendType === 'calendar' && selectedDates.length === 0) || isLoading;

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
          // height: '800px',
          maxWidth: '100%',
          maxHeight: '100%',
        },
      }}
      open={isOpen}
      onClose={handleClose}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        id="import-csv-file-title"
      >
        {t('resources.employees.suspend_employees_title')}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: theme => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
        }}
      >
        <FormControl>
          <FormLabel>{t('resources.employees.suspend_dialog.type_title')}</FormLabel>
          <RadioGroup value={suspendType} onChange={onTypeChange}>
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label={t('resources.employees.suspend_dialog.manual_type')}
            />
            <FormControlLabel
              value="calendar"
              control={<Radio />}
              label={t('resources.employees.suspend_dialog.calendar_type')}
            />
          </RadioGroup>
        </FormControl>
        {suspendType === 'calendar' && (
          <FormControl>
            <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
              <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
                <CustomCalendar
                  selectedDates={selectedDates}
                  onChange={handleCalendarChange}
                  hidePastDays
                  multiple
                />
              </Box>
              <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
                <Box>
                  {selectedDates.map((date, index) => {
                    const day = `${String(date.getDate()).padStart(2, '0')}`;
                    const month = `${String(date.getMonth() + 1).padStart(2, '0')}`;
                    const year = `${date.getFullYear()}`;
                    const fullDate = `${day}.${month}.${year}`;

                    return (
                      <Chip
                        key={index}
                        label={fullDate}
                        onDelete={() => handleRemoveSelectedDay(date)}
                        style={{ margin: '5px' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </FormControl>
        )}
        <FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'end', marginTop: 2 }}>
            <Box>
              <Button
                type="button"
                variant="contained"
                color="error"
                disabled={buttonDisabled}
                onClick={handleCancel}
              >
                {t('resources.employees.suspend_dialog.cancel_button')}
              </Button>
            </Box>
            <Box sx={{ marginLeft: 1 }}>
              <Button
                type="button"
                variant="contained"
                color="success"
                disabled={buttonDisabled}
                onClick={handleApply}
              >
                {t('resources.employees.suspend_dialog.apply_button')}
              </Button>
            </Box>
          </Box>
        </FormControl>
      </DialogContent>
    </Dialog>
  );
}

export default SuspendEmployees;
